import CommonModal from 'components/common/CommonModal';
import ContainerHeader from 'components/common/ContainerHeader';
import DefaultButton from 'components/common/DefaultButton';
import DefaultText from 'components/common/DefaultText';
import Pagination from 'components/common/Pagination';
import ScreenLayout from 'components/common/ScreenLayout';
import {getAuditLogs, retryAuditLogSync} from 'function/api';
import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useRecoilValue} from 'recoil';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {userInfoState} from 'utils/Atoms';
import {minmax} from 'utils/minmax';

const ChangeHistory = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({});
  const navigate = useNavigate();
  const userInfo = useRecoilValue(userInfoState);

  const [data, setData] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [entityType, setEntityType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    if (!userInfo?.accessToken) {
      navigate('/');
    }
  }, []);

  useEffect(() => {
    if (userInfo?.accessToken) {
      handleSearch();
    }
  }, [userInfo, currentPage]);

  const handleSearch = async () => {
    const result = await getAuditLogs({
      userInfo,
      setIsLoading,
      setErrorModal,
      entityType: entityType || undefined,
      from: fromDate || undefined,
      to: toDate || undefined,
      page: currentPage,
      limit: 20,
    });
    if (result) {
      setData(result.items);
      setTotalPage(Math.ceil((result.total || 0) / 20) || 1);
    }
  };

  /** audit log 단건 재시도 → 응답으로 해당 행 syncResult in-place 갱신 (97 A1) */
  const handleRetrySync = async (logId: string) => {
    if (!logId) return;
    const result = await retryAuditLogSync({
      userInfo,
      setIsLoading,
      setErrorModal,
      logId,
    });
    if (!result) return;
    setData((prev: any[] | null) =>
      Array.isArray(prev)
        ? prev.map((row) =>
            row._id === logId
              ? {...row, syncResult: result.syncResult}
              : row,
          )
        : prev,
    );
  };

  const handleResetFilters = () => {
    setEntityType('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  /** 권한 출처 스코프. API 가 `Contractor:CO_OWNER` 처럼 `스코프:역할` 로 보낸다. */
  const scopeLabel = (scope: string) => {
    switch (scope) {
      case 'Contractor': return '시공사';
      case 'PartnerCompany': return '협력사';
      case 'TongPlace': return '현장';
      case 'Team': return '팀';
      default: return scope;
    }
  };

  const baseRoleLabel = (role: string) => {
    switch (role.toUpperCase()) {
      case 'ADMIN': return '백오피스';
      case 'SALES_MANAGER': return '영업';
      case 'OWNER': return '운영관리자';
      case 'CO_OWNER': return '공동운영관리자';
      case 'MANAGER': return '관리자';
      case 'COMMON_MEMBER': return '근로자';
      case 'VIEWER': return '뷰어';
      default: return role;
    }
  };

  /**
   * 행위자 역할 표기.
   * 상위 조직 권한으로 하위 리소스를 조작할 수 있으므로(RoleGuard 에스컬레이션),
   * "어느 조직 권한으로 했는지"까지 보여야 추적이 된다. 예: `시공사 공동운영관리자`.
   * 스코프 없는 예전 값(`OWNER` 등)도 그대로 표시한다.
   */
  const roleLabel = (role?: string) => {
    // documents 계열은 소문자 'unknown' 을 기록한다(전체 314건 중 297건). 대소문자 무관 처리.
    if (!role || role.toUpperCase() === 'UNKNOWN') return '역할미상';
    const sep = role.indexOf(':');
    if (sep > 0) {
      return `${scopeLabel(role.slice(0, sep))} ${baseRoleLabel(role.slice(sep + 1))}`;
    }
    return baseRoleLabel(role);
  };

  /** 전체 경로는 title(툴팁)로 두고, 표에는 어느 화면에서 왔는지만 짧게 보여준다. */
  const endpointLabel = (endpoint?: string) => {
    if (!endpoint) return '-';
    if (endpoint.includes('/admins/')) return '백오피스';
    if (endpoint.includes('/tong-place-permissions/manage')) return '현장 운영자';
    if (endpoint.includes('/contractor-permissions/manage')) return '시공사 운영자';
    if (endpoint.includes('/partner-company-permissions/manage')) return '협력사 운영자';
    if (endpoint.includes('/tong-places/manage')) return '현장 정보';
    if (endpoint.includes('/contractors/update-infos')) return '시공사 정보';
    if (endpoint.includes('/partner-companies/')) return '협력사 정보';
    if (endpoint.includes('/documents/')) return '문서';
    return endpoint.split(' ').pop()?.split('/').slice(0, 3).join('/') || '-';
  };

  const entityTypeLabel = (type: string) => {
    switch (type) {
      case 'User': return '사용자';
      case 'TongPlace': return '현장';
      case 'Contractor': return '시공사';
      case 'PartnerCompany': return '협력사';
      default: return type;
    }
  };

  return (
    <ScreenLayout isLoading={isLoading} errorModal={errorModal}>
      <Container>
        <ContainerHeader mainTitle="변경 이력" subTitle="감사 로그" />

        <Inner>
          <DefaultText
            text="변경 이력 조회"
            textStyle={{
              fontSize: minmax(25),
              fontWeight: 'bold',
              borderBottom: `3px solid ${colors.primary}`,
              width: 'fit-content',
              marginBottom: minmax(20),
            }}
          />

          <FilterRow>
            <FilterLabel>대상 유형</FilterLabel>
            <select
              value={entityType}
              onChange={e => { setEntityType(e.target.value); setCurrentPage(1); }}
              style={{padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: `1px solid ${colors.lightGrey}`}}
            >
              <option value="">전체</option>
              <option value="User">사용자</option>
              <option value="TongPlace">현장</option>
              <option value="Contractor">시공사</option>
              <option value="PartnerCompany">협력사</option>
            </select>

            <FilterLabel>기간</FilterLabel>
            <DateInput
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFromDate(e.target.value)}
            />
            <span>~</span>
            <DateInput
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToDate(e.target.value)}
            />

            <DefaultButton
              text="search"
              actived={1}
              buttonStyle={{backgroundColor: colors.primary2, padding: '8px 20px'}}
              textStyle={{fontSize: minmax(20)}}
              onClick={() => { setCurrentPage(1); handleSearch(); }}
            />
            <DefaultButton
              text="초기화"
              actived={1}
              buttonStyle={{backgroundColor: colors.darkGray, padding: '8px 16px'}}
              textStyle={{fontSize: minmax(18), color: colors.white}}
              onClick={handleResetFilters}
            />
          </FilterRow>

          <Table>
            <thead>
              <Tr>
                <Th style={{width: '12%'}}>일시</Th>
                <Th style={{width: '14%'}}>변경자</Th>
                <Th style={{width: '8%'}}>대상 유형</Th>
                <Th style={{width: '12%'}}>대상</Th>
                <Th style={{width: '12%'}}>현장</Th>
                <Th style={{width: '8%'}}>항목</Th>
                <Th style={{width: '22%'}}>변경 내용</Th>
                <Th style={{width: '7%'}}>경로</Th>
                <Th style={{width: '5%'}}>동기화</Th>
              </Tr>
            </thead>
            <tbody>
              {data && data.length > 0 ? (
                data.map((log: any, idx: number) => (
                  <Tr key={idx}>
                    <Td>{formatDate(log.changedAt)}</Td>
                    <Td>
                      <div>{log.changedBy?.userName || '-'}</div>
                      <SubText title={log.changedBy?.userUUID || ''}>
                        {roleLabel(log.changedBy?.role)}
                        {log.changedBy?.userUUID
                          ? ` · ${String(log.changedBy.userUUID).slice(0, 8)}`
                          : ''}
                      </SubText>
                    </Td>
                    <Td>{entityTypeLabel(log.target?.entityType)}</Td>
                    <Td>
                      <div>{log.target?.entityName || '-'}</div>
                      <SubText title={log.target?.entityUUID || ''}>
                        {log.target?.entityUUID
                          ? String(log.target.entityUUID).slice(0, 8)
                          : ''}
                      </SubText>
                    </Td>
                    <Td>
                      {log.target?.tongPlaceName ? (
                        <div>{log.target.tongPlaceName}</div>
                      ) : null}
                      {log.target?.tongPlaceUUID ? (
                        <SubText title={log.target.tongPlaceUUID}>
                          {String(log.target.tongPlaceUUID).slice(0, 8)}
                        </SubText>
                      ) : (
                        !log.target?.tongPlaceName && '-'
                      )}
                    </Td>
                    <Td>
                      {log.changes?.map((c: any) => c.fieldLabel).join(', ') || '-'}
                    </Td>
                    <Td>
                      {log.changes?.map((c: any, ci: number) => (
                        <ChangeItem key={ci}>
                          <OldValue>{c.oldValue}</OldValue>
                          <Arrow>&rarr;</Arrow>
                          <NewValue>{c.newValue}</NewValue>
                        </ChangeItem>
                      ))}
                    </Td>
                    <Td>
                      <SubText title={log.apiEndpoint || ''}>
                        {endpointLabel(log.apiEndpoint)}
                      </SubText>
                    </Td>
                    <Td>
                      {log.syncResult ? (
                        <SyncCell>
                          <SyncBadge status={log.syncResult.status}>
                            {log.syncResult.status === 'success' ? '✓' : '!'}{' '}
                            {log.syncResult.affectedDocuments}건
                          </SyncBadge>
                          {log.syncResult.status !== 'success' && (
                            <RetryButton
                              type="button"
                              disabled={isLoading}
                              title={
                                log.syncResult.error
                                  ? `이전 오류: ${log.syncResult.error}`
                                  : '재시도'
                              }
                              onClick={() => handleRetrySync(log._id)}>
                              재시도
                            </RetryButton>
                          )}
                        </SyncCell>
                      ) : '-'}
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={9} style={{textAlign: 'center', padding: minmax(40)}}>
                    변경 이력이 없습니다
                  </Td>
                </Tr>
              )}
            </tbody>
          </Table>

          <PaginationWrapper>
            <Pagination
              currentPage={currentPage}
              totalPage={totalPage}
              setCurrentPage={setCurrentPage}
            />
          </PaginationWrapper>
        </Inner>
      </Container>
    </ScreenLayout>
  );
};

export default ChangeHistory;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  margin-bottom: ${minmax(50)};
`;

const Inner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${minmax(20)};
`;

const FilterRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${minmax(10)};
  margin-bottom: ${minmax(20)};
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  font-size: ${minmax(18)};
  font-weight: 500;
  color: ${colors.darkGray};
`;

const DateInput = styled.input`
  padding: 8px 12px;
  font-size: 14px;
  border-radius: 4px;
  border: 1px solid ${colors.lightGrey};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${minmax(20)};
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.lightGrey};
`;

const Th = styled.th`
  padding: ${minmax(12)} ${minmax(8)};
  text-align: left;
  font-weight: bold;
  background-color: ${colors.lighterGray};
  font-size: ${minmax(18)};
`;

const Td = styled.td`
  padding: ${minmax(10)} ${minmax(8)};
  vertical-align: top;
  font-size: ${minmax(18)};
`;

/** 행위자 UUID·역할, 대상 UUID, 호출 경로처럼 보조 식별 정보를 작게 표기한다. */
const SubText = styled.div`
  margin-top: ${minmax(2)};
  font-size: ${minmax(14)};
  color: ${colors.darkGray};
  word-break: break-all;
`;

const ChangeItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${minmax(6)};
  margin-bottom: 2px;
`;

const OldValue = styled.span`
  color: ${colors.darkGray};
  text-decoration: line-through;
`;

const Arrow = styled.span`
  color: ${colors.gray};
`;

const NewValue = styled.span`
  color: ${colors.primary};
  font-weight: bold;
`;

const SyncCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${minmax(6)};
`;

const SyncBadge = styled.span<{status: string}>`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${minmax(16)};
  font-weight: 600;
  background-color: ${({status}) =>
    status === 'success' || status === 'completed'
      ? '#e8f5e9'
      : status === 'partial'
        ? '#fff3e0'
        : '#ffebee'};
  color: ${({status}) =>
    status === 'success' || status === 'completed'
      ? '#2e7d32'
      : status === 'partial'
        ? '#e65100'
        : '#c62828'};
  border: 1px solid
    ${({status}) =>
      status === 'success' || status === 'completed'
        ? '#a5d6a7'
        : status === 'partial'
          ? '#ffcc80'
          : '#ef9a9a'};
`;

const RetryButton = styled.button`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${minmax(14)};
  background-color: ${colors.primary2};
  color: ${colors.white};
  border: none;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${minmax(20)};
`;
